import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { REACTION_EMOJI, REACTION_MEANING } from '../api/types'
import type { Message, ReactionName } from '../api/types'
import { copyLabel, useCopyState } from '../components/copy'
import { radius } from '../components/primitives/shape'
import { holdsReaction, PICKER_REACTIONS } from '../components/Reactions'
import { displayName } from './format'

/** How far down a finger drags the sheet before letting go closes it. */
const SWIPE_TO_CLOSE = 60

/** Each action's line icon, drawn on a 24px grid. */
const ICONS = {
  reply: <><path d="M9 17l-5-5 5-5" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></>,
  mention: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" /></>,
  direct: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="M22 7l-10 6L2 7" /></>,
  copy: <><rect x="8" y="8" width="14" height="14" rx="2" /><path d="M4 16a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2" /></>,
}

function Action({ icon, onClick, children }: { icon: keyof typeof ICONS; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 16, width: '100%', minHeight: 'var(--tap-target)',
        padding: '0 22px', textAlign: 'left', fontSize: 15, color: 'var(--text)', background: 'none', border: 'none' }}
    >
      <svg
        width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: 'none', color: 'var(--text-soft)' }}
      >
        {ICONS[icon]}
      </svg>
      {children}
    </button>
  )
}

/**
 * What a long press on a message opens on a touch screen, in place of the
 * icons a pointer reveals on hover. Portalled to the body: a message row
 * animates in with a transform, and a fixed box inside one is fixed to the row.
 *
 * The dim closes only on a tap that began on it. The finger that held the
 * message lifts after the sheet is up, and the click it ends in lands on the
 * dim without ever having pressed it.
 *
 * Focus comes back where it was on a dismissal; an action that moved it on
 * (a mention focuses the composer) keeps it there.
 */
export function MessageSheet({ message, me, onClose, onToggle, onReply, onMention, onOpenDirect }: {
  message: Message
  me: string
  onClose: () => void
  onToggle: (reaction: ReactionName, held: boolean) => void
  /** Absent inside a thread, where the reply box is. */
  onReply?: () => void
  onMention?: () => void
  /** Only for the reader's own agent. */
  onOpenDirect?: () => void
}) {
  const sheet = useRef<HTMLDivElement>(null)
  const pressedDim = useRef(false)
  const swipeFrom = useRef<number | null>(null)
  const [copied, copy] = useCopyState()

  useEffect(() => {
    const returnTo = document.activeElement
    sheet.current?.focus()
    return () => {
      if (returnTo instanceof HTMLElement && document.activeElement === document.body) returnTo.focus()
    }
  }, [])

  const andClose = (action: () => void) => () => {
    action()
    onClose()
  }

  return createPortal(
    <div
      onPointerDown={(event) => { pressedDim.current = event.target === event.currentTarget }}
      onClick={(event) => {
        if (pressedDim.current && event.target === event.currentTarget) onClose()
      }}
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'flex-end' }}
    >
      <div
        ref={sheet}
        role="dialog"
        aria-modal="true"
        aria-label={`Message from ${message.sender}`}
        tabIndex={-1}
        onKeyDown={(event) => {
          if (event.key !== 'Escape') return
          event.stopPropagation()
          onClose()
        }}
        onPointerDown={(event) => { swipeFrom.current = event.clientY }}
        onPointerUp={(event) => {
          if (swipeFrom.current !== null && event.clientY - swipeFrom.current > SWIPE_TO_CLOSE) onClose()
        }}
        style={{
          width: '100%', outline: 'none', touchAction: 'none', background: 'var(--ground-raised)',
          borderTop: '1px solid var(--border-strong)', borderRadius: `${radius.xl} ${radius.xl} 0 0`,
          padding: '10px 0 calc(10px + var(--safe-bottom))',
        }}
      >
        <div style={{ width: 36, height: 4, margin: '0 auto 16px', borderRadius: 2, background: 'var(--border-strong)' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0 18px 16px', marginBottom: 6, borderBottom: '1px solid var(--border)' }}>
          {PICKER_REACTIONS.map((name) => {
            const held = holdsReaction(message.reactions, name, me)
            return (
              <button
                type="button"
                key={name}
                aria-label={name}
                aria-pressed={held}
                title={REACTION_MEANING[name]}
                onClick={andClose(() => onToggle(name, held))}
                style={{
                  width: 52, height: 52, borderRadius: 'var(--radius-face)', fontSize: 22,
                  background: held ? 'var(--accent-wash)' : 'var(--panel-strong)',
                  border: `1px solid ${held ? 'var(--accent-edge)' : 'transparent'}`,
                }}
              >
                {REACTION_EMOJI[name]}
              </button>
            )
          })}
        </div>
        {onReply && <Action icon="reply" onClick={andClose(onReply)}>Reply in thread</Action>}
        {onMention && <Action icon="mention" onClick={andClose(onMention)}>Mention {displayName(message.sender, message.sender_nickname)}</Action>}
        {onOpenDirect && <Action icon="direct" onClick={andClose(onOpenDirect)}>Send a direct message</Action>}
        <Action icon="copy" onClick={() => copy(message.body)}>{copyLabel(copied, 'Copy text')}</Action>
      </div>
    </div>,
    document.body,
  )
}
